import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useSyncExternalStore } from 'react';
import { z } from 'zod';

import { ModeToggle } from '@apps/web/components/mode_toggle';
import {
    PlaybookRefreshButton,
    PlaybooksDocument,
} from '@apps/web/components/playbooks/playbooks_document';
import { AgentRulesCard } from '@apps/web/components/settings/agent_rules_card';
import {
    SettingsLoadingState,
    SettingsPageShell,
} from '@apps/web/features/settings';
import {
    useCanDirectEditPlaybooks,
    useCanEditPlaybooks,
} from '@apps/web/hooks/use_can_edit_playbooks';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { ModeOverrideProvider, modeStore } from '@apps/web/lib/mode_store';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

const searchSchema = z.object({
    playbook: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

export const Route = createFileRoute('/_app/settings/agent-rules')({
    validateSearch: searchSchema,
    component: PlaybooksSettingsPage,
});

function PlaybooksSettingsPage() {
    const { playbook: initialPlaybookId } = Route.useSearch();
    const organizationId = useOrganizationId();
    const canDirectEditPlaybooks = useCanDirectEditPlaybooks(organizationId);
    const canEditPlaybooks = useCanEditPlaybooks(organizationId);
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings-agent-rules-page',
    });
    const playbookAsRules = useFeatureFlag(
        'playbook_as_rules',
        organization?.features,
    );
    const agentV3Enabled = useFeatureFlag(
        'agent_v3_enabled',
        organization?.features,
    );
    const hideTestModeToggle = useFeatureFlag(
        'hide_test_mode_toggle',
        organization?.features,
    );
    const storedMode = useSyncExternalStore(
        modeStore.subscribe,
        modeStore.getMode,
    );

    useEffect(() => {
        if (hideTestModeToggle && storedMode === 'sandbox') {
            modeStore.setMode('live');
        }
    }, [hideTestModeToggle, storedMode]);

    const isWaiting = hideTestModeToggle && storedMode !== 'live';

    const page = (
        <SettingsPageShell
            title="Playbook"
            maxWidth={playbookAsRules ? 'default' : 'wide'}
            headerAction={
                isWaiting ? undefined : (
                    <div className="flex items-center gap-3">
                        {organizationId && playbookAsRules ? (
                            <PlaybookRefreshButton
                                organizationId={organizationId}
                            />
                        ) : null}
                        {!hideTestModeToggle ? (
                            <div className="w-44">
                                <ModeToggle />
                            </div>
                        ) : null}
                    </div>
                )
            }
        >
            {isWaiting ? (
                <SettingsLoadingState />
            ) : organizationId && playbookAsRules ? (
                <PlaybooksDocument
                    organizationId={organizationId}
                    initialPlaybookId={initialPlaybookId ?? null}
                    canAskPhoebe={canEditPlaybooks && agentV3Enabled}
                />
            ) : organizationId ? (
                <AgentRulesCard
                    organizationId={organizationId}
                    initialPlaybookId={initialPlaybookId ?? null}
                    inSettingsPage={true}
                    canEdit={canEditPlaybooks}
                    canDirectEdit={canDirectEditPlaybooks}
                />
            ) : null}
        </SettingsPageShell>
    );

    return hideTestModeToggle ? (
        <ModeOverrideProvider mode="live">{page}</ModeOverrideProvider>
    ) : (
        page
    );
}
